import { Box, Divider, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import ScalarIcon from 'icons/Scalar';
import { useGenerationEffortOptions } from 'services/hooks/scalarAi';

// Attached to superuser advanced reasoning picks only; named efforts resolve
// their prompt set server-side (EFFORT_REGISTRY).
const ADVANCED_PROMPT_SET = 'rules_aligned_v1';

export type EffortSelection =
  | { kind: 'effort'; effort: string }
  | { kind: 'advanced'; pipeline: string; model: string | null };

export type EffortPayload =
  | Record<string, never>
  | { effort: string }
  | { pipeline: string; model?: string; prompt_set?: string };

export const buildEffortPayload = (selection: EffortSelection | null): EffortPayload => {
  if (!selection) {
    // Untouched selector (or hidden selector): send nothing - the backend
    // resolves the firm's default effort, which is also what was preselected.
    return {};
  }
  if (selection.kind === 'effort') {
    return { effort: selection.effort };
  }
  return selection.pipeline === 'reasoning' && selection.model
    ? { pipeline: 'reasoning', model: selection.model, prompt_set: ADVANCED_PROMPT_SET }
    : { pipeline: selection.pipeline };
};

const EFFORT_PREFIX = 'effort:';
const ADVANCED_PREFIX = 'advanced:';

const advancedKey = (pipeline: string, model: string | null) => `${ADVANCED_PREFIX}${pipeline}|${model ?? ''}`;

// Menu values are flat strings, so a selection round-trips through
// selectionKey -> <Select value> -> parseKey without a lookup table.
const selectionKey = (selection: EffortSelection) =>
  selection.kind === 'effort'
    ? `${EFFORT_PREFIX}${selection.effort}`
    : advancedKey(selection.pipeline, selection.model);

const parseKey = (key: string): EffortSelection => {
  if (key.startsWith(EFFORT_PREFIX)) {
    return { kind: 'effort', effort: key.slice(EFFORT_PREFIX.length) };
  }
  // Split on the FIRST separator only: model ids are vendor-namespaced and must
  // survive the round trip verbatim.
  const rest = key.slice(ADVANCED_PREFIX.length);
  const separator = rest.indexOf('|');
  return { kind: 'advanced', pipeline: rest.slice(0, separator), model: rest.slice(separator + 1) || null };
};

interface EffortSelectorProps {
  firmId: number | null;
  value: EffortSelection | null;
  onChange: (next: EffortSelection) => void;
}

const EffortSelector = ({ firmId, value, onChange }: EffortSelectorProps) => {
  const { data } = useGenerationEffortOptions(firmId);

  // Hidden entirely when the server says so: callers must not reserve space or
  // mention effort (fastest-only firms never learn higher tiers exist).
  if (!data?.show_selector) {
    return null;
  }

  // No value yet means the selector is untouched: show the firm default, which
  // is exactly what the backend applies when the payload omits effort.
  const selectedKey = value ? selectionKey(value) : `${EFFORT_PREFIX}${data.default_effort}`;

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="generation-effort-label">Effort</InputLabel>
      <Select
        labelId="generation-effort-label"
        label="Effort"
        value={selectedKey}
        onChange={event => onChange(parseKey(String(event.target.value)))}>
        {data.options.map(option => (
          <MenuItem key={option.effort} value={`${EFFORT_PREFIX}${option.effort}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.model_name ? `${option.label}: ${option.model_name}` : option.label}
              {option.beyond_firm_max && (
                <Box component="span" data-testid="effort-beyond-max-icon" sx={{ display: 'inline-flex' }}>
                  <ScalarIcon />
                </Box>
              )}
            </Box>
          </MenuItem>
        ))}
        {data.advanced_models.length > 0 && <Divider />}
        {data.advanced_models.map(entry => (
          <MenuItem key={advancedKey(entry.pipeline, entry.value)} value={advancedKey(entry.pipeline, entry.value)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {entry.label}
              <Box component="span" data-testid="effort-advanced-icon" sx={{ display: 'inline-flex' }}>
                <ScalarIcon />
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default EffortSelector;
